import { z } from "zod";
import { CaseStudyItem, CaseStudySection } from "./case-study";

/**
 * Request payload for importing case study from URL
 */
export interface ImportCaseStudyRequest {
  url: string;
}

/**
 * Scraped content from Firecrawl
 */
export interface ScrapedContent {
  markdown: string;
  html: string;
  metadata?: {
    title?: string;
    description?: string;
    ogImage?: string;
    language?: string;
  };
  links?: string[];
  images?: string[];
}

/**
 * Structured case study data extracted by AI
 */
export interface ExtractedCaseStudy {
  title: string;
  description: string;
  coverImage: string;
  sections: Array<{
    type: string;
    title: string;
    content: string;
    images: Array<{
      url: string;
      caption?: string;
    }>;
  }>;
}

/**
 * Response from import edge function
 */
export interface ImportCaseStudyResponse {
  success: boolean;
  data?: Partial<CaseStudyItem>;
  error?: string;
  eventId?: string;
  metadata?: {
    scrapedAt: string;
    sourceUrl: string;
    imagesFound: number;
    sectionsExtracted: number;
  };
}

/**
 * Zod schema for validating imported case study data
 */
export const ImportedCaseStudySchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  cover_image: z.string().url("Invalid cover image URL").optional(),
  sections: z.array(
    z.object({
      type: z.string(),
      title: z.string(),
      content: z.string(),
      images: z.array(
        z.object({
          url: z.string().url(),
          caption: z.string().optional(),
        })
      ).optional().default([]),
    })
  ).optional().default([]),
});

export type ValidatedImportedCaseStudy = z.infer<typeof ImportedCaseStudySchema>;

/**
 * Error categories for import process
 */
export type ImportErrorCategory =
  | "url_invalid"
  | "url_unreachable"
  | "scraping_failed"
  | "content_extraction_failed"
  | "ai_processing_failed"
  | "validation_failed"
  | "rate_limit"
  | "unknown";

/**
 * Import error structure
 */
export interface ImportError {
  category: ImportErrorCategory;
  message: string;
  details?: string;
  suggestion?: string;
}

/**
 * Import state for UI
 */
export type ImportState = "idle" | "scanning" | "processing" | "success" | "failed";
